"use client"

// Inspired by react-hot-toast library
import * as React from "react"
import { useContext } from "react"
import { AuthContext } from "../context/auth-context"
import { logSystemError } from "../lib/data"

import type {
  ToastActionElement,
  ToastProps,
} from "../components/ui/toast"

const TOAST_LIMIT = 1
const TOAST_REMOVE_DELAY = 1000000

type ToasterToast = ToastProps & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: ToastActionElement
  nnId?: string
  code?: string
  file?: string
}

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

type ActionType = typeof actionTypes

type Action =
  | {
    type: ActionType["ADD_TOAST"]
    toast: ToasterToast
  }
  | {
    type: ActionType["UPDATE_TOAST"]
    toast: Partial<ToasterToast>
  }
  | {
    type: ActionType["DISMISS_TOAST"]
    toastId?: ToasterToast["id"]
  }
  | {
    type: ActionType["REMOVE_TOAST"]
    toastId?: ToasterToast["id"]
  }

interface State {
  toasts: ToasterToast[]
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({
      type: "REMOVE_TOAST",
      toastId: toastId,
    })
  }, TOAST_REMOVE_DELAY)

  toastTimeouts.set(toastId, timeout)
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      }

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      }

    case "DISMISS_TOAST": {
      const { toastId } = action

      // ! Side effects ! - This could be extracted into a dismissToast() action,
      // but I'll keep it here for simplicity
      if (toastId) {
        addToRemoveQueue(toastId)
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id)
        })
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
              ...t,
              open: false,
            }
            : t
        ),
      }
    }
    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        }
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      }
  }
}

const listeners: Array<(state: State) => void> = []

let memoryState: State = { toasts: [] }

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => {
    listener(memoryState)
  })
}

type Toast = Omit<ToasterToast, "id">

function toast({ ...props }: Toast & { nnId?: string; file?: string }) {
  const id = genId()

  const update = (props: ToasterToast) =>
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id },
    })
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id })

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss()
      },
    },
  })

  return {
    id: id,
    dismiss,
    update,
  }
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState)
  const auth = useContext(AuthContext)
  const user = auth?.user

  React.useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [state])

  const roleAwareToast = React.useCallback(
    ({ nnId, code, file, ...props }: Toast & { nnId?: string; code?: string; file?: string }) => {
      const isSystemError = props.variant === "destructive"
      const isAdmin = user?.role === "admin" || user?.role === "glavni_trener"

      let finalProps = { ...props }
      const finalId = nnId || code
      const displayId = finalId ? ` [${finalId}]` : ""

      // 1. Logging (Always log system errors)
      if (isSystemError) {
        logSystemError({
          nnId: finalId || "UNKNOWN",
          userId: user?.id || "GUEST",
          userName: user?.name || "GUEST",
          userRole: user?.role || "GUEST",
          title: String(props.title || ""),
          description: String(props.description || ""),
          file: file || "UNKNOWN",
        })
      }

      // 2. Role-based display modification
      if (isSystemError && !isAdmin) {
        // For users/trainers: change variant to warning, simplify message
        finalProps.variant = "warning"
        finalProps.duration = 4000

        // If description is empty or generic, use the default message
        const currentDesc = String(props.description || "").toLowerCase()
        if (!props.description || currentDesc === "n/a" || currentDesc === "") {
          finalProps.description = "Aplikacija nema ovu funkcionalnost" + displayId
        } else {
          finalProps.description = String(props.description) + displayId
        }
      } else if (nnId) {
        // Append ID to title or description for admins/success messages
        finalProps.description = (finalProps.description ? String(finalProps.description) : "") + displayId
      }

      return toast(finalProps)
    },
    [user]
  )

  return {
    ...state,
    toast: roleAwareToast,
    dismiss: (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId }),
  }
}

export { useToast, toast }
